import { Injectable } from '@angular/core'

import { catchError, map, Observable, throwError } from 'rxjs'

import {
  SkillMap,
  AssessmentTag,
  AssessmentTagMaster,
  mappingAssessment,
  mappingAssessmentSummary,
  mappingAssessmentTags,
  mappingSkillMap,
} from './assessment.mapping'
import {
  CreateAssessmentGQL,
  UpdateAssessmentGQL,
  ArchiveAssessmentGQL,
  ArchiveAssessmentsGQL,
  GetAssessmentGQL,
  GetSkillMapsGQL,
  SearchAssessmentsGQL,
  SearchAssessmentStatus,
  TagMatchMode,
  GetAccountsGQL,
  MarkContentAsAccessedGQL,
  ExportAssessmentsGQL,
  ExportAssessmentSummariesGQL,
  AssessmentExecutionType,
  AssessmentTagMastersGQL,
  AddAssessmentTagGQL,
  RemoveAssessmentTagGQL,
} from '../../../_generated/graphql'
import { assertGraphqlData } from '../../util/assertGraphqlData'
import { throwIfCustomError } from '../../util/throwIfCustomError'

/** getAssessment で確定済みフィールドの再取得を抑制するためのオプション */
export type GetAssessmentOptions = {
  skipTranscript?: boolean
  skipSummary?: boolean
}

@Injectable({
  providedIn: 'root',
})
export class AssessmentService {
  constructor(
    private getAssessmentGQL: GetAssessmentGQL,
    private searchAssessmentsGQL: SearchAssessmentsGQL,
    private getSkillMapsGQL: GetSkillMapsGQL,
    private createAssessmentGQL: CreateAssessmentGQL,
    private updateAssessmentGQL: UpdateAssessmentGQL,
    private getAccountsGQL: GetAccountsGQL,
    private markContentAsAccessedGQL: MarkContentAsAccessedGQL,
    private exportAssessmentsGQL: ExportAssessmentsGQL,
    private exportAssessmentSummariesGQL: ExportAssessmentSummariesGQL,
    private archiveAssessmentGQL: ArchiveAssessmentGQL,
    private archiveAssessmentsGQL: ArchiveAssessmentsGQL,
    private assessmentTagMastersGQL: AssessmentTagMastersGQL,
    private addAssessmentTagGQL: AddAssessmentTagGQL,
    private removeAssessmentTagGQL: RemoveAssessmentTagGQL,
  ) {}

  getSkillMaps(): Observable<SkillMap[]> {
    return this.getSkillMapsGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.getSkillMaps.map((skillMap) => {
          return mappingSkillMap(skillMap)
        })
      }),
    )
  }

  getAccounts() {
    return this.getAccountsGQL
      .fetch({
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.getAccounts.map((account) => {
            return { name: account.name }
          })
        }),
      )
  }

  getAssessment(
    assessmentId: string,
    { skipTranscript = false, skipSummary = false }: GetAssessmentOptions = {},
  ) {
    return this.getAssessmentGQL
      .fetch({
        variables: { assessmentId, skipTranscript, skipSummary },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          const assessment = res.data?.getAssessment

          if (!assessment) {
            alert('Assessment not found')
            throw new Error('Assessment not found')
          }
          return mappingAssessment(assessment)
        }),
      )
  }

  searchAssessments(
    query: string,
    pagination: { limit: number; offset: number },
    status: 'ALL' | 'PROGRESS_STATUSES',
    sort?: { sortField: string; sortOrder: 'asc' | 'desc' },
    useCache = false,
    tagIds?: string[],
    reportToUserId: string | null = null,
    tagMatch?: TagMatchMode | null,
  ) {
    return this.searchAssessmentsGQL
      .fetch({
        variables: {
          query,
          pagination,
          status:
            status === 'ALL'
              ? SearchAssessmentStatus.All
              : SearchAssessmentStatus.ProgressStatuses,
          sort,
          tagIds,
          reportToUserId,
          ...(typeof tagMatch === 'undefined' ? {} : { tagMatch }),
        },
        fetchPolicy: useCache ? 'cache-first' : 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.searchAssessments.map((assessment) => {
            return mappingAssessmentSummary(assessment)
          })
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** テナントで利用可能なアセスメントタグマスターを取得する */
  getAssessmentTagMasters(): Observable<AssessmentTagMaster[]> {
    return this.assessmentTagMastersGQL.fetch({ fetchPolicy: 'no-cache' }).pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.assessmentTagMasters.map((tagMaster) => ({
          id: tagMaster.id,
          name: tagMaster.name,
        }))
      }),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
    )
  }

  /** アセスメントへタグを追加し、更新後のタグ一覧を返す */
  addAssessmentTag(
    assessmentId: string,
    tagMasterId: string,
  ): Observable<AssessmentTag[]> {
    return this.addAssessmentTagGQL
      .mutate({ variables: { assessmentId, tagMasterId } })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return mappingAssessmentTags(res.data.addAssessmentTag.tags)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** アセスメントからタグを削除し、更新後のタグ一覧を返す */
  removeAssessmentTag(
    assessmentId: string,
    tagMasterId: string,
  ): Observable<AssessmentTag[]> {
    return this.removeAssessmentTagGQL
      .mutate({ variables: { assessmentId, tagMasterId } })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return mappingAssessmentTags(res.data.removeAssessmentTag.tags)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  createAssessment(param: {
    userId: string
    businessMeetingName: string
    accountName: string | null
    recordDate: Date
    fileName: string
    // 評価スキップテナントは SkillMap 選択 UI が無いため null を渡す
    skillMapId: string | null
    phaseIds: string[]
    assessmentExecutionType: AssessmentExecutionType
  }) {
    return this.createAssessmentGQL.mutate({
      variables: {
        input: {
          userId: param.userId,
          title: param.businessMeetingName,
          accountName: param.accountName,
          recordDate: param.recordDate,
          fileName: param.fileName,
          skillMapId: param.skillMapId,
          phaseIds: param.phaseIds,
          assessmentExecutionType: param.assessmentExecutionType,
        },
      },
    })
  }

  updateAssessment(param: {
    assessmentId: string
    businessMeetingName: string
  }) {
    return this.updateAssessmentGQL.mutate({
      variables: {
        input: {
          id: param.assessmentId,
          title: param.businessMeetingName,
        },
      },
    })
  }

  markContentAsAccessed(recommendedContentId: string) {
    return this.markContentAsAccessedGQL.mutate({
      variables: {
        recommendedContentId,
      },
    })
  }

  exportAssessments(
    pagination?: { limit: number; offset: number },
    param?: {
      startDate: Date | null
      endDate: Date | null
    },
  ) {
    return this.exportAssessmentsGQL.fetch({ variables: { pagination, param } })
  }

  exportAssessmentSummaries(
    pagination?: { limit: number; offset: number },
    param?: {
      startDate: Date | null
      endDate: Date | null
    },
  ) {
    return this.exportAssessmentSummariesGQL.fetch({
      variables: { pagination, param },
    })
  }

  deleteAssessment(assessmentId: string) {
    return this.archiveAssessmentGQL.mutate({ variables: { assessmentId } })
  }

  deleteAssessments(assessmentIds: string[]) {
    return this.archiveAssessmentsGQL.mutate({ variables: { assessmentIds } })
  }
}
